import type { CountdownVictoryId, VictoryId } from '@sd/content';
import type { Hex } from './hex';

/**
 * Victories (GDD §10): countdowns everyone sees, the Dyson swarm of the
 * technological victory, and the final standings by Prestige. Pure types and
 * helpers; the match drives them from `step`.
 */
export interface Countdown {
  readonly playerId: string;
  readonly kind: CountdownVictoryId;
  /** The victory counts at this tick, if its condition still holds. */
  readonly endsAt: number;
  /**
   * Held by sanctions on the issuer (#133): `endsAt` moves on with the clock, and
   * the countdown goes on from where it stood once they end, if the share still holds.
   */
  readonly paused?: boolean;
}

/** A Dyson swarm under construction in the corona. */
export interface Megastructure {
  readonly owner: string;
  readonly hex: Hex;
  /** Finished stages, 0..MEGASTRUCTURE.stages. */
  readonly stages: number;
  /** The stage being built, done at this tick; null while waiting for resources. */
  readonly buildingUntil: number | null;
  /** A hostile fleet holding the hex since this tick. */
  readonly hold: { readonly by: string; readonly since: number } | null;
}

export interface Standing {
  readonly playerId: string;
  readonly prestige: number;
}

export interface MatchResult {
  readonly kind: VictoryId;
  /** Null when everyone lost (collapse). */
  readonly winner: string | null;
  /** The winner and their teammates (#137): everyone who won; empty on a collapse. */
  readonly winners: readonly string[];
  readonly tick: number;
  /** Everyone by Prestige, highest first. */
  readonly standings: readonly Standing[];
}

/** Players by Prestige, highest first; ties by id so the order never wavers. */
export function standings(
  players: readonly { readonly id: string; readonly prestige: number }[],
): Standing[] {
  return players
    .map((p) => ({ playerId: p.id, prestige: p.prestige }))
    .sort((a, b) => b.prestige - a.prestige || a.playerId.localeCompare(b.playerId));
}
